import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';

import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {openInBrowser} from '../../../../../../../srcCommon/browser/openInBrowser';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../services/tracking';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';

/*
 * Props.
 */

interface BulkActionConfirmationDialogProps {
  onRequestClose: Handler;
  onConfirmClick: Handler;
  actionType: BulkActionTypesEnum;
}

/*
 * Component.
 */

export const BulkActionConfirmationDialog: FC<BulkActionConfirmationDialogProps> = (props) => {
  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MULTI_SELECT_MODAL_WARNING_DISPLAYED, {
      type: 'long_job',
    });
  });

  return (
    <OkCancelDialog
      title={bulkActionTypeToDialogTitleMap[props.actionType]}
      okButtonLabel={<Trans id="bulkaction-confirmation-proceed">Proceed</Trans>}
      onOkClick={props.onConfirmClick}
      onRequestClose={props.onRequestClose}
      interactionId="archive-team-dialog"
    >
      <Trans id="bulkaction-confirmation-body">
        This mass action will take up to 20 minutes to complete and rules will not be triggered.{' '}
        <InlineLinkButton
          onClick={() =>
            openInBrowser(
              'https://help.frontapp.com/t/g9hkhpp/select-all-conversations-and-apply-bulk-actions',
            )
          }
        >
          <Trans id="bulkaction-confirmationtitle-learnmore">Learn more</Trans>
        </InlineLinkButton>
        <br />
        <br />
        Mass actions will only impact up to 10,000 conversations at a time. Do you want to proceed?
      </Trans>
    </OkCancelDialog>
  );
};

/*
 * Helpers.
 */

const bulkActionTypeToDialogTitleMap: Record<BulkActionTypesEnum, ReactNode> = {
  [BulkActionTypesEnum.ARCHIVE]: <Trans id="bulkaction-confirmationtitle-archive">Mass archive</Trans>,
  [BulkActionTypesEnum.REOPEN]: <Trans id="bulkaction-confirmationtitle-reopen">Mass reopen</Trans>,
  [BulkActionTypesEnum.SNOOZE]: <Trans id="bulkaction-confirmationtitle-snooze">Mass snooze</Trans>,
  [BulkActionTypesEnum.UNSNOOZE]: <Trans id="bulkaction-confirmationtitle-unsnooze">Mass unsnooze</Trans>,
  [BulkActionTypesEnum.TRASH]: <Trans id="bulkaction-confirmationtitle-trash">Mass trash</Trans>,
  [BulkActionTypesEnum.MOVE]: <Trans id="bulkaction-confirmationtitle-move">Mass move</Trans>,
  [BulkActionTypesEnum.ASSIGN]: <Trans id="bulkaction-confirmationtitle-assign">Mass assign</Trans>,
  [BulkActionTypesEnum.UNASSIGN]: <Trans id="bulkaction-confirmationtitle-unassign">Mass unassign</Trans>,
  [BulkActionTypesEnum.TAG]: <Trans id="bulkaction-confirmationtitle-tag">Mass tag</Trans>,
  [BulkActionTypesEnum.UNTAG]: <Trans id="bulkaction-confirmationtitle-untag">Mass untag</Trans>,
  [BulkActionTypesEnum.STAR]: <Trans id="bulkaction-confirmationtitle-star">Mass star</Trans>,
  [BulkActionTypesEnum.UNSTAR]: <Trans id="bulkaction-confirmationtitle-unstar">Mass unstar</Trans>,
  [BulkActionTypesEnum.MARK_UNREAD]: (
    <Trans id="bulkaction-confirmationtitle-markunread">Mass mark as unread</Trans>
  ),
  [BulkActionTypesEnum.MARK_READ]: (
    <Trans id="bulkaction-confirmationtitle-markread">Mass mark as read</Trans>
  ),
  [BulkActionTypesEnum.SPAM]: <Trans id="bulkaction-confirmationtitle-spam">Mass mark as spam</Trans>,
};
